import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CardInfo from '../../../components/CardInfo/CardInfo'
import EditAction from '../../ProfilePage/components/EditAction'
import PropTypes from 'prop-types'
import { XIcon } from '@heroicons/react/outline'
import clsx from 'clsx'
import DragAndDrop from '../../../components/DragAndDrop/DragAndDrop'
import useGlobalLoading from '../../../hooks/useGlobalLoading'
import useUploadFile from '../../../hooks/useUploadFile'
import { logErrorOnServer } from '../../../helpers/serverLogging'
import ImagePreviewModal from '../../../components/CarouselImages/components/ImagePreviewModal'
import { ArrowsExpandIcon } from '@heroicons/react/solid'
import { useNetworkStatus } from '../../../utils/networkStatus'

const logError = logErrorOnServer('Edit Asset Image')

const AssetImage = ({ assetImage, onSave }) => {
    const [isEnabled, setIsEnabled] = useState(false)
    const [currentImage, setCurrentImage] = useState(assetImage)
    const [selectedFile, setSelectedFile] = useState({
        file: null,
        fileName: null,
    })
    const { isOffline } = useNetworkStatus()

    useEffect(() => {
        if (assetImage) {
            setCurrentImage(assetImage)
        }
    }, [assetImage])

    const { changeLoading } = useGlobalLoading()
    const { handleUpdateFile } = useUploadFile()
    const [modalPreviewOpen, setModalPreviewOpen] = useState(false)

    const disabledButton =
        (currentImage === null || currentImage === assetImage) &&
        selectedFile?.file == null

    const imageUpdatedSuccess = () => {
        setIsEnabled(false)
    }

    const handleSubmit = e => {
        e?.preventDefault()
        onSave &&
            onSave(
                {
                    photoId: selectedFile?.fileName,
                },
                imageUpdatedSuccess,
                'image',
            )
    }

    const resetSelectedFile = () => {
        setSelectedFile({
            file: null,
            fileName: null,
        })
    }

    const onRemove = () => {
        if (selectedFile?.file) {
            resetSelectedFile()
        } else {
            setCurrentImage(null)
        }
    }

    useEffect(() => {
        if (selectedFile?.file == null) {
            setCurrentImage(assetImage)
        } else if (selectedFile?.file) {
            resetSelectedFile()
            setCurrentImage(assetImage)
        } else if (!isEnabled) {
            setCurrentImage(null)
        }
    }, [isEnabled])

    useEffect(() => {
        if (assetImage) {
            setCurrentImage(assetImage)
        }
    }, [assetImage])

    const renderRemoveImageButton = () => {
        return (
            <div
                onClick={onRemove}
                role="button"
                className="w-6 h-6 z-50 flex items-center justify-center rounded-full bg-error cursor-pointer  absolute -top-2 -right-2"
            >
                <XIcon className="text-white w-4 h-4" />
            </div>
        )
    }

    const handleImageSuccess = (resp, file) => {
        const result = resp?.data
        setSelectedFile({
            file: file,
            fileName: result.filename,
        })
        changeLoading(false)
    }

    const handleImageError = result => {
        const errorMessage =
            result.toString().indexOf('File is too large') > -1
                ? 'File is too large. 8MB Max'
                : result.toString().indexOf('422') > -1
                ? result.toString().substring(10)
                : 'Unable to upload file.'
        //setError(errorMessage)
        changeLoading(false)
        logError(errorMessage)
    }

    const setFilesFunc = useCallback(async val => {
        await Promise.all(
            val?.map(file => {
                const formData = new FormData()
                formData.append('file', file)
                changeLoading(true)
                return handleUpdateFile(
                    formData,
                    handleImageSuccess,
                    handleImageError,
                    file,
                )
            }),
        )
    }, [])

    const getMemoSelectedPhoto = useMemo(() => {
        if (selectedFile?.file) {
            const srcImage = URL.createObjectURL(selectedFile?.file)
            return srcImage
        }
        return null
    }, [selectedFile])

    const renderContentAssetImage = () => {
        if (isOffline) {
            return (
                <div className="w-full h-full bg-gray-200 rounded-xl flex items-center justify-center">
                    <p className="text-sm text-gray-600">No image available</p>
                </div>
            )
        }
        return (
            <div className="w-full h-full flex max-w-full max-h-full items-center justify-center overflow-visible relative transition-all">
                {isEnabled &&
                    (currentImage || selectedFile?.file) &&
                    renderRemoveImageButton()}
                {currentImage || selectedFile?.file ? (
                    <div className="w-full h-full relative">
                        <ImagePreviewModal
                            isOpen={modalPreviewOpen}
                            setIsOpen={setModalPreviewOpen}
                            url={
                                currentImage
                                    ? currentImage
                                    : getMemoSelectedPhoto
                            }
                        />
                        <button
                            onClick={() => setModalPreviewOpen(true)}
                            type="button"
                            className="flex items-center cursor-pointer outline-none justify-center border border-grayLines bg-white shadow-sm rounded-lg p-2  absolute bottom-4 left-4 z-50"
                        >
                            <ArrowsExpandIcon className="mr-0 m-0 text-textDarkerGray w-6 h-6" />
                        </button>

                        <img
                            className={clsx(
                                'object-cover w-full h-full rounded-2xl transition-all',
                                isEnabled && 'border border-grayLines',
                            )}
                            src={
                                currentImage
                                    ? currentImage
                                    : getMemoSelectedPhoto
                            }
                        />
                    </div>
                ) : (
                    isEnabled && (
                        <div className="flex w-full h-full flex-col md:p-0 items-center justify-center transition-all">
                            <DragAndDrop setFiles={setFilesFunc} />
                        </div>
                    )
                )}
            </div>
        )
    }

    return (
        <form className="w-full h-full overflow-hidden" onSubmit={handleSubmit}>
            <CardInfo
                title="Asset Image"
                content={renderContentAssetImage()}
                titleClass={clsx(isEnabled && 'md:w-auto w-full')}
                headerActions={[
                    <EditAction
                        setEnabled={setIsEnabled}
                        isValid={isEnabled && !disabledButton}
                        isEnabled={isEnabled}
                        key="editButtonImage"
                    />,
                ]}
            />
        </form>
    )
}

AssetImage.propTypes = {
    assetImage: PropTypes.string,
    onSave: PropTypes.func,
    setAssetImage: PropTypes.func,
}

export default AssetImage
